import { useState, useEffect, useRef } from 'react';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { toast } from '../../lib/toast';
import { Upload, FileText, Check, X, AlertCircle, RefreshCw, Database, ArrowRight, Download } from 'lucide-react';
import Papa from 'papaparse';
import { AnalysisQueuePage } from './AnalysisQueuePage';

interface BatchImportStep {
  id: string;
  name: string;
  description: string;
  component: React.ReactNode;
  isCompleted: boolean;
  isActive: boolean;
}

interface CSVRow {
  name: string;
  keyword: string;
  url?: string;
  additional_keywords?: string;
  location?: string;
  language?: string;
  [key: string]: any;
}

interface ImportPreviewProps {
  data: CSVRow[];
  onConfirm: () => void;
  onCancel: () => void;
}

interface ProjectOption {
  id: string;
  name: string;
}

interface Location {
  id: string;
  name: string;
  parentId?: string;
  countryCode: string;
  type: 'Country' | 'Region';
}

const LOCATIONS: Location[] = [
  { id: "2804", name: "Ukraine", countryCode: "UA", type: "Country" },
  { id: "21118", name: "Kyiv city, Ukraine", parentId: "2804", countryCode: "UA", type: "Region" },
  { id: "21580", name: "Kyiv Oblast, Ukraine", parentId: "2804", countryCode: "UA", type: "Region" }
];

const LANGUAGES = [
  { code: "uk", name: "Ukrainian" },
  { code: "en", name: "English" },
  { code: "ru", name: "Russian" }
];

function ImportPreview({ data, onConfirm, onCancel }: ImportPreviewProps) {
  if (!data.length) return null;

  // Define column widths
  const columnWidths: Record<string, string> = {
    additional_keywords: '250px',
    name: '200px',
    keyword: '150px',
    url: '200px',
    location: '100px',
    language: '100px'
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {Object.keys(data[0]).map((header) => (
                <th
                  key={header}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  style={{ 
                    width: columnWidths[header] || 'auto',
                    minWidth: columnWidths[header] || 'auto',
                    maxWidth: columnWidths[header] || 'auto'
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.slice(0, 5).map((row, rowIndex) => (
              <tr key={rowIndex}>
                {Object.entries(row).map(([key, value], cellIndex) => (
                  <td
                    key={cellIndex}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                    style={{ 
                      width: columnWidths[key] || 'auto',
                      minWidth: columnWidths[key] || 'auto',
                      maxWidth: columnWidths[key] || 'auto',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <div className="truncate" title={value?.toString() || ''}>
                      {value?.toString() || ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {data.length > 5 && (
        <p className="text-sm text-gray-500">
          Showing 5 of {data.length} rows
        </p>
      )}

      <div className="flex justify-end space-x-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Confirm Import
        </button>
      </div>
    </div>
  );
}

export function BatchFunctionsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [csvData, setCSVData] = useState<CSVRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [defaultLocation, setDefaultLocation] = useState<string>("2804"); // Default to Ukraine
  const [defaultLanguage, setDefaultLanguage] = useState<string>("uk"); // Default to Ukrainian
  const [addToQueue, setAddToQueue] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'import' | 'queue'>('import');

  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<BatchImportStep[]>([
    {
      id: 'select-project',
      name: 'Select Project',
      description: 'Choose which project to import analyses into',
      component: null,
      isCompleted: false,
      isActive: true
    },
    {
      id: 'upload-csv',
      name: 'Upload CSV',
      description: 'Upload a CSV file with analysis data',
      component: null,
      isCompleted: false,
      isActive: false
    },
    {
      id: 'preview-data',
      name: 'Preview Data',
      description: 'Review the data before importing',
      component: null,
      isCompleted: false,
      isActive: false
    },
    {
      id: 'import-data',
      name: 'Import Data',
      description: 'Import the data into the database',
      component: null,
      isCompleted: false,
      isActive: false
    }
  ]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabaseClient
          .from('projects')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setProjects(data || []);
      } catch (err) {
        console.error('Error loading projects:', err);
        setError(err instanceof Error ? err.message : 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: ',', // Explicitly set comma as delimiter
      complete: (results) => {
        // Validate required columns
        const headers = results.meta.fields || [];
        if (!headers.includes('name') || !headers.includes('keyword')) {
          setError('CSV file must include "name" and "keyword" columns');
          setIsUploading(false);
          return;
        }

        // Process data
        const data = results.data as CSVRow[];
        
        // Add default location and language if missing
        const processedData = data.map(row => ({
          ...row,
          location: row.location || defaultLocation,
          language: row.language || defaultLanguage
        }));
        
        setCSVData(processedData);
        
        // Update steps
        const updatedSteps = [...steps];
        updatedSteps[1].isCompleted = true;
        updatedSteps[2].isActive = true;
        setSteps(updatedSteps);
        
        // Move to next step
        setCurrentStep(2);
        setIsUploading(false);
      },
      error: (error) => {
        console.error('Error parsing CSV:', error);
        setError('Failed to parse CSV file: ' + error.message);
        setIsUploading(false);
      }
    });
  };

  const handleProjectSelection = (projectId: string) => {
    setSelectedProject(projectId);
    
    // Update steps
    const updatedSteps = [...steps];
    updatedSteps[0].isCompleted = true;
    updatedSteps[1].isActive = true;
    setSteps(updatedSteps);
    
    // Move to next step
    setCurrentStep(1);
  };

  const handleConfirmImport = async () => {
    if (!selectedProject || !csvData.length) {
      setError('Project and CSV data are required');
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    setImportedCount(0);
    setFailedCount(0);
    setError(null);

    // Update steps
    const updatedSteps = [...steps];
    updatedSteps[2].isCompleted = true;
    updatedSteps[3].isActive = true;
    setSteps(updatedSteps);
    
    // Move to next step
    setCurrentStep(3);

    try {
      // Process in batches to avoid overwhelming the database
      const batchSize = 10;
      const totalItems = csvData.length;
      
      for (let i = 0; i < totalItems; i += batchSize) {
        const batch = csvData.slice(i, i + batchSize);
        
        // Process each item in the batch
        const batchPromises = batch.map(async (row) => {
          try {
            // Process additional keywords - handle comma-separated values
            let additionalKeywords: string[] = [];
            if (row.additional_keywords) {
              // Use a regex to handle quoted values containing commas
              const regex = /(?:^|,)(?:"([^"]*(?:""[^"]*)*)"|([^,]*))/g;
              let match;
              while ((match = regex.exec(row.additional_keywords)) !== null) {
                const value = match[1] || match[2]; // Either quoted or unquoted value
                if (value && value.trim()) {
                  additionalKeywords.push(value.trim().replace(/""/g, '"')); // Handle double quotes
                }
              }
            }

            // Prepare analysis data
            const analysisData = {
              project_id: selectedProject,
              name: row.name,
              keyword: row.keyword,
              url: row.url || null,
              additional_keywords: additionalKeywords,
              status: 'pending',
              status_type: 'task is ready',
              language: row.language || defaultLanguage,
              location: row.location || defaultLocation
            };

            // Insert into database
            const { data: newAnalysis, error } = await supabaseClient
              .from('analyses')
              .insert(analysisData)
              .select('id')
              .single();

            if (error) throw error;
            
            // Add to queue if option is selected
            if (addToQueue && newAnalysis?.id) {
              const { error: queueError } = await supabaseClient
                .from('analysis_queue')
                .insert({
                  analysis_id: newAnalysis.id,
                  priority: 5,
                  status: 'queued'
                });
              
              if (queueError) {
                console.error('Error adding to queue:', queueError);
                // Continue even if queue insertion fails
              }
            }
            
            return { success: true };
          } catch (err) {
            console.error('Error importing row:', row, err);
            return { success: false, error: err };
          }
        });

        // Wait for all items in the batch to complete
        const results = await Promise.all(batchPromises);
        
        // Update counts
        const successCount = results.filter(r => r.success).length;
        setImportedCount(prev => prev + successCount);
        setFailedCount(prev => prev + (results.length - successCount));
        
        // Update progress
        const newProgress = Math.min(100, Math.round(((i + batch.length) / totalItems) * 100));
        setImportProgress(newProgress);
      }

      // Update steps
      updatedSteps[3].isCompleted = true;
      setSteps(updatedSteps);
      
      toast.success(`Import completed: ${importedCount} analyses imported, ${failedCount} failed`);
    } catch (err) {
      console.error('Error during import:', err);
      setError(err instanceof Error ? err.message : 'Failed to import data');
      toast.error('Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancelImport = () => {
    setCSVData([]);
    
    // Reset steps
    const updatedSteps = [...steps];
    updatedSteps[1].isCompleted = false;
    updatedSteps[2].isActive = false;
    setSteps(updatedSteps);
    
    // Go back to upload step
    setCurrentStep(1);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReset = () => {
    setCSVData([]);
    setSelectedProject('');
    setImportedCount(0);
    setFailedCount(0);
    setImportProgress(0);
    
    // Reset steps
    const resetSteps = steps.map((step, index) => ({
      ...step,
      isCompleted: false,
      isActive: index === 0
    }));
    setSteps(resetSteps);
    
    // Go back to first step
    setCurrentStep(0);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    // Create CSV template content
    const headers = ['name', 'keyword', 'url', 'additional_keywords', 'location', 'language'];
    const sampleRow = [
      'Sample Analysis',
      'sample keyword',
      'https://example.com',
      'keyword1, keyword2, "keyword with, comma"',
      '2804',
      'uk'
    ];
    
    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'analysis_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Batch Functions</h2>

        <div className="mb-6 border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('import')}
              className={`flex-1 px-4 py-3 text-sm font-medium text-center ${
                activeTab === 'import'
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="h-4 w-4 mx-auto mb-1" />
              Import Data
            </button>
            <button
              onClick={() => setActiveTab('queue')}
              className={`flex-1 px-4 py-3 text-sm font-medium text-center ${
                activeTab === 'queue'
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Database className="h-4 w-4 mx-auto mb-1" />
              Analysis Queue
            </button>
          </nav>
        </div>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        {activeTab === 'import' ? (
          <>
            <div className="mb-8">
              <div className="relative">
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                  <div
                    style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500 transition-all duration-500"
                  ></div>
                </div>
                <div className="flex justify-between">
                  {steps.map((step, index) => (
                    <div
                      key={step.id}
                      className={`flex flex-col items-center ${
                        index <= currentStep ? 'text-indigo-600' : 'text-gray-400'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${
                          step.isCompleted
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : index === currentStep
                            ? 'border-indigo-600 text-indigo-600'
                            : 'border-gray-300 text-gray-300'
                        }`}
                      >
                        {step.isCompleted ? (
                          <Check className="h-6 w-6" />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                      <div className="text-xs mt-2 font-medium">{step.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8">
              {currentStep === 0 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Select Project</h3>
                  <p className="text-sm text-gray-500">
                    Choose the project where you want to import analyses.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          selectedProject === project.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50'
                        }`}
                        onClick={() => handleProjectSelection(project.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {project.name}
                            </h3>
                          </div>
                          {selectedProject === project.id && (
                            <Check className="h-5 w-5 text-indigo-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {projects.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No projects found. Please create a project first.</p>
                    </div>
                  )}
                </div>
              )}

              {currentStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Upload CSV File</h3>
                  <p className="text-sm text-gray-500">
                    Upload a CSV file with the required columns. You can download a template below.
                  </p>

                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Default values if not specified in CSV:
                        </p>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-sm font-medium text-blue-700">Default Location</label>
                            <select
                              value={defaultLocation}
                              onChange={(e) => setDefaultLocation(e.target.value)}
                              className="mt-1 block w-full rounded-md border-blue-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              {LOCATIONS.map(loc => (
                                <option key={loc.id} value={loc.id}>
                                  {loc.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700">Default Language</label>
                            <select
                              value={defaultLanguage}
                              onChange={(e) => setDefaultLanguage(e.target.value)}
                              className="mt-1 block w-full rounded-md border-blue-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              {LANGUAGES.map(lang => (
                                <option key={lang.code} value={lang.code}>
                                  {lang.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="mt-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={addToQueue}
                              onChange={(e) => setAddToQueue(e.target.checked)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-blue-700">
                              Add imported analyses to processing queue
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <div className="space-y-4 text-center">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex flex-col items-center text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md bg-white font-medium text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:text-indigo-500"
                        >
                          <span>Upload a CSV file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            disabled={isUploading}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">CSV up to 10MB</p>
                    </div>
                  </div>

                  {isUploading && (
                    <div className="flex items-center justify-center">
                      <RefreshCw className="animate-spin h-5 w-5 mr-3 text-indigo-500" />
                      <span>Uploading...</span>
                    </div>
                  )}

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-yellow-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          CSV file must include the following columns:
                        </p>
                        <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                          <li>name (required) - The name of the analysis</li>
                          <li>keyword (required) - The target keyword</li>
                          <li>url (optional) - The URL to analyze</li>
                          <li>additional_keywords (optional) - Comma-separated list of additional keywords</li>
                          <li>location (optional) - Location ID for SERP analysis (default: {defaultLocation})</li>
                          <li>language (optional) - Language code for SERP analysis (default: {defaultLanguage})</li>
                        </ul>
                        <p className="mt-2 text-sm text-yellow-700">
                          <strong>Note:</strong> For additional_keywords with commas, enclose the value in double quotes: "keyword1, keyword2"
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <div>
                      <button
                        onClick={() => {
                          setSelectedProject('');
                          setCurrentStep(0);
                          
                          // Update steps
                          const updatedSteps = [...steps];
                          updatedSteps[0].isCompleted = false;
                          updatedSteps[1].isActive = false;
                          setSteps(updatedSteps);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Back
                      </button>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleDownloadTemplate}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Download className="h-4 w-4 mr-2 inline-block" />
                        Download Template
                      </button>
                      <button
                        onClick={() => {
                          if (fileInputRef.current) {
                            fileInputRef.current.click();
                          }
                        }}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Upload className="h-4 w-4 mr-2 inline-block" />
                        Select File
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Preview Data</h3>
                  <p className="text-sm text-gray-500">
                    Review the data before importing. The first 5 rows are shown below.
                  </p>

                  <ImportPreview
                    data={csvData}
                    onConfirm={handleConfirmImport}
                    onCancel={handleCancelImport}
                  />
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Import Data</h3>
                  <p className="text-sm text-gray-500">
                    Importing {csvData.length} analyses into the selected project.
                  </p>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="mb-4">
                      <div className="flex justify-between text-sm font-medium text-gray-700 mb-1">
                        <span>Progress</span>
                        <span>{importProgress}%</span>
                      </div>
                      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                        <div
                          style={{ width: `${importProgress}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500 transition-all duration-300"
                        ></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-sm font-medium text-green-800">Imported</span>
                        </div>
                        <p className="mt-1 text-2xl font-bold text-green-700">{importedCount}</p>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <X className="h-5 w-5 text-red-500 mr-2" />
                          <span className="text-sm font-medium text-red-800">Failed</span>
                        </div>
                        <p className="mt-1 text-2xl font-bold text-red-700">{failedCount}</p>
                      </div>
                    </div>

                    {isImporting ? (
                      <div className="mt-4 flex items-center justify-center">
                        <RefreshCw className="animate-spin h-5 w-5 mr-3 text-indigo-500" />
                        <span>Importing data...</span>
                      </div>
                    ) : (
                      <div className="mt-6 flex justify-center">
                        <button
                          onClick={handleReset}
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                          Start New Import
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <AnalysisQueuePage />
        )}
      </div>
    </div>
  );
}